import React from 'react';
import { Form, Formik, Field, ErrorMessage } from 'formik';
import { Link, Redirect } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCreateOrgMutation } from '@/queries/auth/useCreateOrgMutation';
import { useAuthStore } from '@/stores/auth-store';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

export default function Signup() {
  const createOrgMutation = useCreateOrgMutation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const validateForm = (values) => {
    const errors = {};
    if (!values.orgName?.trim()) {
      errors.orgName = 'Required';
    }
    if (!values.accountName?.trim()) {
      errors.accountName = 'Required';
    } else if (!EMAIL_RE.test(values.accountName.trim())) {
      errors.accountName = 'Enter a valid email address';
    }
    if (!values.password) {
      errors.password = 'Required';
    } else if (values.password.length < 8) {
      errors.password = 'At least 8 characters';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Required';
    } else if (values.confirmPassword !== values.password) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const submit = (values) => {
    createOrgMutation.mutate(values);
  };

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div className="mx-auto w-full max-w-[960px] px-2 py-4">
      <Formik
        enableReinitialize
        initialValues={{
          orgName: '',
          accountName: '',
          password: '',
          confirmPassword: '',
        }}
        onSubmit={submit}
        validate={validateForm}
      >
        {() => (
          <Form className="admin-form grid gap-6 lg:grid-cols-[1fr_minmax(0,280px)] lg:items-start">
            <div className="flex min-w-0 flex-col gap-6">
              <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
                <h1 className="mb-2 text-[clamp(1.35rem,2vw+0.8rem,1.85rem)] font-bold text-[#121212]">
                  Create your organization
                </h1>
                <p className="mb-6 text-left text-sm text-[var(--festy-muted-text)]">
                  Set up a new organization and administrator account. You will
                  be signed in automatically.
                </p>
                <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
                  <FontAwesomeIcon
                    icon={['fas', 'building']}
                    className="text-[var(--bethere-primary)]"
                  />
                  Organization
                </h2>
                <div className="input-group">
                  <label htmlFor="signup-org-name">Organization name</label>
                  <Field
                    id="signup-org-name"
                    type="text"
                    name="orgName"
                    placeholder="e.g. Noble Applications Inc."
                    autoComplete="organization"
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="orgName" />
                  </div>
                </div>
              </div>

              <div className="glass-panel glass-panel-interactive p-6 sm:p-8">
                <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[#121212]">
                  <FontAwesomeIcon
                    icon={['fas', 'user']}
                    className="text-[var(--bethere-primary)]"
                  />
                  Administrator account
                </h2>
                <div className="input-group">
                  <label htmlFor="signup-email">Email address</label>
                  <Field
                    id="signup-email"
                    type="email"
                    name="accountName"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="accountName" />
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="signup-password">Password</label>
                  <Field
                    id="signup-password"
                    type="password"
                    name="password"
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="password" />
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="signup-confirm">Confirm password</label>
                  <Field
                    id="signup-confirm"
                    type="password"
                    name="confirmPassword"
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                  <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                    <ErrorMessage name="confirmPassword" />
                  </div>
                </div>
              </div>
            </div>

            <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-6">
              <div className="glass-panel glass-panel-interactive p-6 text-left">
                <h3 className="mb-3 text-lg font-bold text-[#121212]">
                  Get started
                </h3>
                <p className="mb-6 text-sm leading-relaxed text-[var(--festy-muted-text)]">
                  Your email becomes the admin login. You can invite team
                  members after signing in.
                </p>
                <button
                  type="submit"
                  disabled={createOrgMutation.isPending}
                  className="inline-flex w-full min-h-[52px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] hover:shadow-md disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={['fas', 'user-plus']} />
                  Create account
                </button>
                <Link
                  to="/login"
                  className="mt-3 flex min-h-[52px] w-full items-center justify-center rounded-[var(--bethere-radius-md)] border border-[var(--festy-input-border)] bg-white/60 font-bold text-[#121212] shadow-sm transition hover:bg-white/90"
                >
                  Cancel
                </Link>
              </div>
              <div className="glass-panel p-4 text-center">
                <p className="text-sm text-[var(--festy-muted-text)]">
                  Already have an account?
                </p>
                <Link
                  to="/login"
                  className="mt-1 inline-block text-sm font-bold text-[var(--bethere-primary)] hover:underline"
                >
                  Sign in
                </Link>
              </div>
            </aside>
          </Form>
        )}
      </Formik>
    </div>
  );
}
